// Music + ambient bed module. Streams via HTMLAudioElement (WebAudio for
// short SFX is fine; for 1-5 minute beds the streaming + native decode of
// HTMLAudio is friendlier on memory). The element is routed through the
// shared AudioContext via createMediaElementSource so the music gain and
// master gain still apply.

import lobbyBedUrl from '../assets/music/lobby-bed.mp3?url';
import roundBedUrl from '../assets/music/round-bed.mp3?url';
import roundTransitionStingUrl from '../assets/music/round-transition-sting.mp3?url';
import finalRevealUrl from '../assets/music/final-reveal.mp3?url';
import podiumCelebrationUrl from '../assets/music/podium-celebration.mp3?url';

import { audioBus } from './bus.js';

// 'round' is a looping in-game bed backed by a dedicated round-bed.mp3.
export type MusicName = 'lobby' | 'round' | 'round-transition' | 'final-reveal' | 'podium';

interface MusicSpec { url: string; loop: boolean; gain: number }

const SPEC_LOBBY: MusicSpec = { url: lobbyBedUrl, loop: true, gain: 1.0 };
const SPEC_ROUND_TRANSITION: MusicSpec = { url: roundTransitionStingUrl, loop: false, gain: 1.0 };
const SPEC_FINAL_REVEAL: MusicSpec = { url: finalRevealUrl, loop: false, gain: 1.0 };
const SPEC_PODIUM: MusicSpec = { url: podiumCelebrationUrl, loop: false, gain: 1.0 };
// Dedicated low-intensity round bed; loops under active gameplay.
const SPEC_ROUND: MusicSpec = { url: roundBedUrl, loop: true, gain: 1.0 };

function specOf(name: MusicName): MusicSpec {
  if (name === 'lobby') return SPEC_LOBBY;
  if (name === 'round') return SPEC_ROUND;
  if (name === 'round-transition') return SPEC_ROUND_TRANSITION;
  if (name === 'final-reveal') return SPEC_FINAL_REVEAL;
  return SPEC_PODIUM;
}

interface Track {
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
  trackGain: GainNode | null;
}

const tracks = new Map<MusicName, Track>();
let current: { name: MusicName; track: Track } | null = null;

function getTrack(name: MusicName): Track {
  let t = tracks.get(name);
  if (t) return t;
  const spec = specOf(name);
  // Each track owns its own HTMLAudioElement; createMediaElementSource is
  // one-per-element, so lobby + round (sharing lobby-bed.mp3 during fallback)
  // need separate Audio elements for independent playheads.
  const el = new Audio(spec.url);
  el.preload = 'auto';
  el.loop = spec.loop;
  let source: MediaElementAudioSourceNode | null = null;
  let trackGain: GainNode | null = null;
  const ctx = audioBus.context();
  if (ctx) {
    try {
      source = ctx.createMediaElementSource(el);
      // Per-track gain so the round-bed fallback ducks below the lobby bed
      // without changing the bus music gain.
      trackGain = ctx.createGain();
      trackGain.gain.value = spec.gain;
      source.connect(trackGain);
      trackGain.connect(audioBus.musicGain());
    } catch (err) {
      console.warn(`[music] media-element-source failed for ${name}:`, err);
    }
  }
  t = { el, source, trackGain };
  tracks.set(name, t);
  return t;
}

export const music = {
  play(name: MusicName): void {
    if (current && current.name === name && !current.track.el.paused) return;
    if (current) {
      try { current.track.el.pause(); } catch { /* ignore */ }
    }
    const track = getTrack(name);
    current = { name, track };
    if (!audioBus.isUnlocked()) {
      void audioBus.unlock();
    }
    try {
      track.el.currentTime = 0;
      const p = track.el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          console.debug(`[music] autoplay blocked for ${name}:`, err);
        });
      }
    } catch (err) {
      console.warn(`[music] play ${name} failed:`, err);
    }
  },
  stop(): void {
    if (!current) return;
    try { current.track.el.pause(); } catch { /* ignore */ }
    current = null;
  },
  /**
   * Re-attempt playback after the audio bus unlocks. Called by the autoplay
   * overlay so the lobby bed (or whatever was queued) starts as soon as the
   * user clicks.
   */
  resumeIfPending(): void {
    if (!current) return;
    const el = current.track.el;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* still blocked, ignore */ });
      }
    }
  },
};
